import type { ReactNode } from "react";
import { CompactCheck, CompactSelect } from "../inspector/rows";

/**
 * The timeline's controls in the studio's own vocabulary. It was the last
 * Advanced panel still drawn with the Ring Builder's kit (uppercase tracked
 * labels, bronze pill toggles, underlined selects) — a different product
 * inside one group. Same props as the kit pieces it replaces, so the panel's
 * logic is untouched.
 */

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="mb-1.5 mt-3 text-[12px] font-semibold text-ed-text-2">{children}</div>;
}

export function Select({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: { id: string; label: string }[];
  onChange: (v: string) => void;
  variant?: "underline" | "boxed";
  label?: string;
}) {
  return (
    <div className="flex w-full" aria-label={label}>
      <CompactSelect value={value} options={options} onChange={onChange} />
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return <CompactCheck checked={checked} onChange={onChange} label={label} />;
}

/** Scrub / speed: a labelled range whose drag bounds the caller can hook. */
export function Slider({
  label,
  min,
  max,
  step,
  value,
  unit,
  onChange,
  onDragStart,
  onDragEnd,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
  onChange: (v: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const shown = step >= 1 ? String(Math.round(value)) : value.toFixed(2);
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="w-[52px] shrink-0 text-[12px] text-ed-text-2">{label}</span>
      <input
        type="range"
        className="lux min-w-0 flex-1"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onPointerDown={() => onDragStart?.()}
        onPointerUp={() => onDragEnd?.()}
        onPointerCancel={() => onDragEnd?.()}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      <span className="w-14 shrink-0 text-right text-[12px] tabular-nums text-ed-text-3">
        {shown}
        {unit ?? ""}
      </span>
    </div>
  );
}
